import { Tooltip } from '@mui/material';

import { COLORS } from './constants';

import { useRetryHauserEQEvaluation } from '@/hooks/hauser-eq';

const font = "'Manrope', sans-serif";

/** The DLQ reprocessor's error blob isn't a fixed schema — pull out
 * whatever human-readable reason is available, defensively. */
export const extractFailureReason = (
  error: Record<string, unknown> | string | null,
): string | null => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  const reason = error.reason;
  return typeof reason === 'string' ? reason : null;
};

export interface ScoringStatusChipsHauserEq {
  status?: string;
  contextStatus?: string;
  conditionStatus?: string;
  contextError?: Record<string, unknown> | string;
  conditionError?: Record<string, unknown> | string;
}

/**
 * Scoring-pipeline status indicator(s) for a property — shared between the
 * admin properties table (PropertiesTab) and the advisor-facing properties
 * page (PropertiesPageNew), since both fetch the same HauserEQ-enriched
 * property list and both audiences need this visibility. Notably, an
 * advisor is often the one uploading documents and needs to know a
 * pipeline failed *before* inviting the buyer in — not just admins running
 * ops — see #19. Deliberately NOT shown on the buyer-visible score-detail
 * pages (Overview/The Home/The Area) — those just say "Data not
 * available" regardless of why, same as always. This is the one place
 * advisors/admins see the failure and can act on it.
 *
 * Renders one chip per affected pipeline with domain language ("Context
 * scoring failed", not a combined "HauserEQ: X" label — advisors/admins
 * know "Condition"/"Context" scoring, not that internal service name),
 * plus a single green "in progress" chip while actively scoring. Renders
 * nothing once everything's done and healthy: signal, not noise. Failed
 * chips are themselves the retry action — click to retry that pipeline.
 */
export function ScoringStatusChips({
  propertyId,
  hauserEq,
}: {
  propertyId: string | undefined;
  hauserEq: ScoringStatusChipsHauserEq | undefined;
}) {
  const retryMutation = useRetryHauserEQEvaluation();

  const chips: Array<{
    key: string;
    label: string;
    color: string;
    reason: string | null;
    retryScoreType?: 'area' | 'home';
  }> = [];

  // Still actively working and nothing's wrong yet — context_status/
  // condition_status aren't set at all during this phase (see hauser-eq
  // #17), only the top-level status. Once a specific pipeline hits
  // trouble it moves into the failed/retrying chips below instead.
  if (hauserEq?.status === 'processing' || hauserEq?.status === 'queued') {
    chips.push({ key: 'processing', label: 'Scoring in progress', color: COLORS.lime, reason: null });
  }

  if (hauserEq?.contextStatus === 'failed') {
    chips.push({
      key: 'context-failed',
      label: 'Context scoring failed',
      color: COLORS.critical,
      reason: extractFailureReason(hauserEq.contextError ?? null),
      retryScoreType: 'area',
    });
  } else if (hauserEq?.contextStatus === 'retrying') {
    chips.push({
      key: 'context-retrying',
      label: 'Context scoring retrying',
      color: COLORS.urgent,
      reason: null,
    });
  }

  if (hauserEq?.conditionStatus === 'failed') {
    chips.push({
      key: 'condition-failed',
      label: 'Condition scoring failed',
      color: COLORS.critical,
      reason: extractFailureReason(hauserEq.conditionError ?? null),
      retryScoreType: 'home',
    });
  } else if (hauserEq?.conditionStatus === 'retrying') {
    chips.push({
      key: 'condition-retrying',
      label: 'Condition scoring retrying',
      color: COLORS.urgent,
      reason: null,
    });
  }

  // Whole-property failure (e.g. propertylens-api-data itself failing) —
  // the Set-Failed-Status safety net, distinct from a single pipeline's
  // context_status/condition_status. Neither of those gets set on this
  // path, so without this it would otherwise show no chip at all despite
  // genuinely having failed. See hauser-eq #17. Not individually
  // retryable here — ambiguous which pipeline to re-queue — so it's
  // informational only; use Rescore Home/Area instead.
  if (hauserEq?.status === 'failed' && chips.length === 0) {
    chips.push({ key: 'top-level-failed', label: 'Scoring failed', color: COLORS.critical, reason: null });
  }

  if (chips.length === 0) return null;

  return (
    <>
      {chips.map((chip) => {
        const isRetryable = !!chip.retryScoreType;
        const isThisPending =
          retryMutation.isPending && retryMutation.variables?.scoreType === chip.retryScoreType;
        const label = isThisPending ? 'Retrying…' : chip.label;
        const tooltip = isRetryable
          ? [chip.reason, 'Click to retry'].filter(Boolean).join(' — ')
          : chip.reason ?? '';

        return (
          <Tooltip key={chip.key} title={tooltip} placement="top" arrow>
            <span
              role={isRetryable ? 'button' : undefined}
              onClick={
                isRetryable && propertyId && !retryMutation.isPending
                  ? (e) => {
                      e.stopPropagation();
                      retryMutation.mutate({ propertyId, scoreType: chip.retryScoreType as 'area' | 'home' });
                    }
                  : undefined
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: `${chip.color}14`,
                color: chip.color,
                border: 0,
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: font,
                cursor: isRetryable ? 'pointer' : 'default',
                opacity: isThisPending ? 0.7 : 1,
              }}>
              <span
                style={{ width: 6, height: 6, borderRadius: '50%', background: chip.color, flexShrink: 0 }}
              />
              {label}
            </span>
          </Tooltip>
        );
      })}
    </>
  );
}
